<template>
  <div class="org-card">
    <div class="org-card__header">
      <span class="org-avatar" :style="{ backgroundColor: org.color }">
        {{ org.initials }}
      </span>
      <span class="org-card__name">{{ org.name }}</span>
      <span class="org-card__badges">
        <span
          v-if="org.isSupplyChain"
          class="org-badge org-badge--supply-chain"
        >Supply Chain</span>
        <span
          v-for="badge in org.licenseLabels"
          :key="badge"
          class="org-badge"
        >{{ badge }}</span>
        <span
          v-if="!org.isSupplyChain"
          v-show="!org.licenseLabels || org.licenseLabels.length === 0"
          class="org-badge org-badge--none"
        >
          {{ $t('licensing.settings.orgs.none') }}
        </span>
      </span>
    </div>

    <div class="org-card__body">
      <div
        v-if="!org.licenses || org.licenses.length === 0"
        class="org-card__empty"
      >
        {{ $t('licensing.settings.orgs.no.licenses') }}
      </div>

      <template v-else>
        <div class="lic-rows" :style="{ gridTemplateColumns: `repeat(${licCols}, 1fr)` }">
          <div
            v-for="lic in org.licenses"
            :key="lic.id"
            class="lic-card"
            :class="{ 'lic-card--expired': lic.isExpired }"
          >
            <div class="lic-card__top">
              <span
                class="lic-card__dot"
                :class="lic.isExpired ? 'lic-card__dot--red' : 'lic-card__dot--green'"
              />
              <span class="lic-card__name">
                {{ lic.skuDetails && lic.skuDetails.displayName || 'Unknown' }}
              </span>
            </div>
            <!-- Platform access: no dates, just "Active" badge -->
            <template v-if="isPlatformAccess(lic)">
              <div class="lic-card__tags">
                <span class="lic-card__tag lic-card__tag--active">Active</span>
              </div>
            </template>
            <template v-else>
            <!-- Dates as compact subtitle -->
            <div class="lic-card__dates">
              {{ formatDateShort(lic.startsAt) }} – {{ formatDateShort(lic.expiresAt) }}
            </div>
            <!-- Time left + usage + capacity as inline tags -->
            <div class="lic-card__tags">
              <v-tooltip top max-width="220" content-class="org-addon-tooltip">
                <template #activator="{ on, attrs }">
                  <span
                    class="lic-card__tag"
                    :class="getTimeLeftClass(lic)"
                    v-bind="attrs"
                    v-on="on"
                  >
                    {{ getTimeLeftLabel(lic) }}
                  </span>
                </template>
                <span>{{ getTimeLeftTooltip(lic) }}</span>
              </v-tooltip>

              <v-tooltip v-if="getUsagePercent(lic) > 0" top max-width="220" content-class="org-addon-tooltip">
                <template #activator="{ on, attrs }">
                  <span
                    class="lic-card__tag"
                    :class="getUsageClass(lic)"
                    v-bind="attrs"
                    v-on="on"
                  >
                    {{ getUsagePercent(lic) }}%
                    <v-icon v-if="getUsagePercent(lic) >= 100" x-small color="error">block</v-icon>
                  </span>
                </template>
                <span>{{ getUsageTooltip(lic) }}</span>
              </v-tooltip>

              <v-tooltip v-if="getCapacityLabel(lic)" top max-width="220" content-class="org-addon-tooltip">
                <template #activator="{ on, attrs }">
                  <span
                    class="lic-card__tag lic-card__tag--cap"
                    v-bind="attrs"
                    v-on="on"
                  >
                    {{ getCapacityLabel(lic) }}
                  </span>
                </template>
                <span>License usage / capacity</span>
              </v-tooltip>
            </div>
            </template>
          </div>
        </div>

        <div class="org-card__addons">
          <span class="org-card__addons-title">
            {{ $t('licensing.settings.addons.title') }}
          </span>
          <div class="org-card__addons-list">
            <v-tooltip
              v-for="addon in addons"
              :key="addon.key"
              top
              max-width="280"
              content-class="org-addon-tooltip"
            >
              <template #activator="{ on, attrs }">
                <span
                  class="org-addon"
                  :class="addon.active ? 'org-addon--active' : 'org-addon--inactive'"
                  v-bind="attrs"
                  v-on="on"
                >
                  <span
                    class="org-addon__dot"
                    :class="addon.active ? 'org-addon__dot--on' : 'org-addon__dot--off'"
                  />
                  {{ addon.label }}
                </span>
              </template>
              <span>{{ $t('licensing.settings.addons.tooltip.' + addon.key) }}</span>
            </v-tooltip>
          </div>
        </div>
      </template>

      <div class="org-card__footer">
        <v-btn
          x-small
          outlined
          color="primary"
          class="activate-btn"
          @click="$emit('activate', org.id)"
        >
          <v-icon x-small left>add</v-icon>
          {{ $t('licensing.settings.activate') }}
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import { ADDON_LABELS, ADDON_KEYS, MODULE_ADDON_KEYS } from '@/components/Settings/licensing-constants';

export default {
  name: 'OrgLicenseCard',
  props: {
    org: {
      type: Object,
      required: true,
    },
  },
  computed: {
    licCols() {
      const n = (this.org.licenses || []).length;
      if (n <= 1) return 1;
      if (n <= 4) return 2;
      return 3;
    },
    addons() {
      const enabled = new Set();
      (this.org.licenses || []).forEach(lic => {
        if (lic.isExpired) return;
        const details = lic.skuDetails || {};
        (details.enabledAddons || []).forEach(a => enabled.add(a));
        // Module-based addons: mark active when the module is licensed
        if (MODULE_ADDON_KEYS.includes(details.module)) {
          enabled.add(details.module);
        }
      });
      return ADDON_KEYS.map(key => ({
        key,
        label: ADDON_LABELS[key],
        active: enabled.has(key),
      }));
    },
  },
  methods: {
    isPlatformAccess(lic) {
      return lic.skuDetails?.module === 'platform_access';
    },
    getCapacityLabel(lic) {
      const d = lic.skuDetails;
      if (!d) return '';
      const usage = this.org.usage || {};
      const parts = [];
      if (d.maxAssets) {
        const used = usage.assetCount != null ? `${usage.assetCount.toLocaleString()} / ` : '';
        parts.push(`${used}${d.maxAssets.toLocaleString()} assets`);
      }
      if (d.maxDomains) {
        const used = usage.domainCount != null ? `${usage.domainCount} / ` : '';
        parts.push(`${used}${d.maxDomains} domains`);
      }
      if (d.keywords) {
        const used = usage.keywordCount != null ? `${usage.keywordCount} / ` : '';
        parts.push(`${used}${d.keywords} keywords`);
      }
      return parts.join(' · ') || (d.tier || '');
    },
    getUsagePercent(lic) {
      const d = lic.skuDetails;
      if (!d) return 0;
      const usage = this.org.usage || {};
      // Pick the most relevant capacity metric for this license module
      if (d.maxAssets && usage.assetCount != null) {
        return Math.min(100, Math.round((usage.assetCount / d.maxAssets) * 100));
      }
      if (d.maxDomains && usage.domainCount != null) {
        return Math.min(100, Math.round((usage.domainCount / d.maxDomains) * 100));
      }
      if (d.keywords && usage.keywordCount != null) {
        return Math.min(100, Math.round((usage.keywordCount / d.keywords) * 100));
      }
      return 0;
    },
    getUsageClass(lic) {
      const pct = this.getUsagePercent(lic);
      if (pct >= 100) return 'lic-card__tag--usage-red';
      if (pct >= 90) return 'lic-card__tag--usage-orange';
      if (pct >= 75) return 'lic-card__tag--usage-yellow';
      return 'lic-card__tag--usage-green';
    },
    getTimeLeftLabel(lic) {
      if (lic.isExpired) return 'Expired';
      const days = this.getDaysLeft(lic);
      if (days > 365) {
        const years = Math.round(days / 365);
        return `${years} year${years > 1 ? 's' : ''}`;
      }
      if (days > 60) {
        const months = Math.round(days / 30);
        return `${months} months`;
      }
      return `${days} days`;
    },
    getTimeLeftClass(lic) {
      if (lic.isExpired) return 'lic-card__tag--red';
      const days = this.getDaysLeft(lic);
      if (days <= 14) return 'lic-card__tag--red';
      if (days <= 30) return 'lic-card__tag--warning';
      return '';
    },
    formatDateShort(dateStr) {
      if (!dateStr) return '—';
      return new Date(dateStr).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: '2-digit',
      });
    },
    getDaysLeft(lic) {
      if (!lic.expiresAt) return 999;
      return Math.ceil(
        (new Date(lic.expiresAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24),
      );
    },
    getTimeLeftTooltip(lic) {
      if (lic.isExpired) return 'This license has expired';
      const days = this.getDaysLeft(lic);
      if (days <= 14) return `Expires in ${days} day${days === 1 ? '' : 's'} — renew urgently`;
      if (days <= 30) return `Expires in ${days} days — renew soon`;
      return `Expires ${this.formatDateShort(lic.expiresAt)} (${days} days remaining)`;
    },
    getUsageTooltip(lic) {
      const pct = this.getUsagePercent(lic);
      const d = lic.skuDetails;
      const usage = this.org.usage || {};
      if (!d) return `${pct}% used`;
      if (d.maxAssets && usage.assetCount != null) return `${usage.assetCount.toLocaleString()} of ${d.maxAssets.toLocaleString()} assets used (${pct}%)`;
      if (d.maxDomains && usage.domainCount != null) return `${usage.domainCount} of ${d.maxDomains} domains used (${pct}%)`;
      if (d.keywords && usage.keywordCount != null) return `${usage.keywordCount} of ${d.keywords} keywords used (${pct}%)`;
      return `${pct}% capacity used`;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/styles/_sizing";
@import "@/assets/styles/_colors";
@import "@/assets/styles/_semantic-sizing";

.org-card {
  background: var(--bg-secondary);
  border: $border-width-thin solid var(--border-primary);
  border-radius: $radius-lg;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 100%;

  &__header {
    width: 100%;
    display: flex;
    align-items: center;
    gap: $space-2-5;
    padding: $space-3 $space-4;
    background: none;
    border: none;
    text-align: left;
    color: var(--text-primary);
  }

  &__name {
    font-family: $font-family-bold;
    font-size: $font-size-base;
    flex: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__badges {
    display: flex;
    flex-wrap: wrap;
    gap: $space-1;
  }

  &__body {
    padding: $space-3 $space-4;
    border-top: $border-width-thin solid var(--border-primary);
    display: flex;
    flex-direction: column;
    gap: $space-3;
    flex: 1;
  }

  &__addons {
    border-top: $border-width-thin solid var(--border-primary);
    padding-top: $space-2-5;
  }

  &__addons-title {
    display: block;
    font-family: $font-family-bold;
    font-size: $font-size-sm;
    color: var(--text-secondary);
    text-transform: uppercase;
    letter-spacing: $letter-spacing-wider;
    margin-bottom: $space-2;
  }

  &__addons-list {
    display: flex;
    flex-wrap: wrap;
    gap: $space-1-5;
  }

  &__footer {
    border-top: $border-width-thin solid var(--border-primary);
    padding-top: $space-2-5;
    margin-top: auto;
  }

  &__empty {
    color: var(--text-secondary);
    font-family: $font-family-regular;
    font-size: $font-size-base;
    padding: $space-6 0;
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
  }
}

.activate-btn {
  text-transform: none;
  letter-spacing: $letter-spacing-normal;
}

// License grid — columns set dynamically via inline style based on license count
.lic-rows {
  display: grid;
  gap: $space-2;
}

.lic-card {
  background: var(--bg-primary);
  border: $border-width-thin solid var(--border-primary);
  border-radius: $radius-6;
  padding: $space-2 $space-2-5;

  &--expired { opacity: 0.55; }

  &__top {
    display: flex;
    align-items: center;
    gap: $space-1-5;
    margin-bottom: $space-1;
  }

  &__dot {
    width: 7px;
    height: 7px;
    border-radius: $radius-full;
    flex-shrink: 0;

    &--green { background: $success-500; }
    &--red { background: $error-500; }
  }

  &__name {
    font-family: $font-family-bold;
    font-size: $font-size-sm;
    color: var(--text-primary);
    flex: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
  }

  &__dates {
    font-family: $font-family-regular;
    font-size: $font-size-sm;
    color: var(--text-secondary);
    margin-bottom: $space-1-5;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__tags {
    display: flex;
    flex-wrap: wrap;
    gap: $space-1;
  }

  &__tag {
    display: inline-flex;
    align-items: center;
    gap: $space-0-5;
    font-family: $font-family-regular;
    font-size: $font-size-sm;
    padding: 2px $space-2;
    border-radius: $radius-full;
    background: var(--bg-tertiary, $gray-100);
    color: var(--text-secondary);
    white-space: nowrap;
    cursor: default;

    // Time left color states
    &--warning { color: $warning-500; background: $warning-50; font-family: $font-family-bold; }
    &--red { color: $error-500; background: $error-50; font-family: $font-family-bold; }

    // Usage badge
    &--usage-green  { color: $success-500; background: $success-50; }
    &--usage-yellow { color: $warning-500; background: $warning-50; }
    &--usage-orange { color: $severity-high; background: $severity-high-bg; }
    &--usage-red    { color: $error-500; background: $error-50; }

    // Capacity — neutral
    &--cap {
      color: var(--text-secondary);
      background: var(--bg-tertiary, $gray-100);
    }

    // Platform access "Active" badge
    &--active {
      color: $success-500;
      background: $success-50;
      font-family: $font-family-bold;
    }
  }
}

.org-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: $avatar-size-sm;
  height: $avatar-size-sm;
  border-radius: $radius-full;
  color: $white;
  font-size: $font-size-xs;
  font-family: $font-family-bold;
  flex-shrink: 0;
}

.org-badge {
  display: inline-block;
  padding: $space-0-5 $space-3;
  border: $border-width-thin solid var(--border-primary);
  border-radius: $radius-md;
  font-family: $font-family-regular;
  font-size: $font-size-sm;
  color: var(--text-primary);

  &--none {
    color: var(--text-secondary);
    border-style: dashed;
  }

  &--supply-chain {
    background: rgba(139, 92, 246, 0.12);
    border-color: rgba(139, 92, 246, 0.4);
    color: #7c3aed;
    font-family: $font-family-bold;
  }
}

.org-addon {
  display: inline-flex;
  align-items: center;
  gap: $space-1-5;
  padding: $space-1 $space-2-5;
  border-radius: $radius-full;
  font-family: $font-family-regular;
  font-size: $font-size-sm;
  white-space: nowrap;
  cursor: default;

  &--active {
    background: $success-50;
    color: $success-500;
  }

  &--inactive {
    background: var(--bg-tertiary, $gray-100);
    color: var(--text-secondary);
  }

  &__dot {
    width: 6px;
    height: 6px;
    border-radius: $radius-full;
    flex-shrink: 0;

    &--on { background: $success-500; }
    &--off { background: var(--text-secondary); opacity: 0.4; }
  }
}
</style>

<style lang="scss">
/* Unscoped — Vuetify tooltips render outside component scope */
.org-addon-tooltip {
  font-size: 14px !important;
  line-height: 1.5 !important;
  padding: 8px 14px !important;
}
</style>
